import { NavigateFunction } from 'react-router-dom'
import { ToolCall } from '../utils/types'

/**
 * A single JSON Schema node. Supports scalars, arrays (`items`) and nested
 * objects (`properties`). Backward compatible with the previous
 * `{ type, description?, enum? }` shape used by existing tool definitions.
 */
export interface JsonSchemaNode {
  type: string
  description?: string
  enum?: string[]
  /** Element schema when `type === 'array'` */
  items?: JsonSchemaNode
  /** Property schemas when `type === 'object'` */
  properties?: Record<string, JsonSchemaNode>
  required?: string[]
  minItems?: number
}

/** JSON Schema object — matches the wire format in body.tools[].parameters */
export interface JsonSchemaObject {
  type: 'object'
  properties: Record<string, JsonSchemaNode>
  required?: string[]
}

/** Serialised into body.tools[] as-is */
export interface FrontendToolDefinition {
  name: string
  description: string
  parameters: JsonSchemaObject
  requiresConfirmation: boolean
}

export interface FrontendToolContext {
  navigate: NavigateFunction
  dispatch: (action: any) => any
  getState: () => any
  fetchApp: (nameOrId: string) => Promise<any>
  addRecentlyOpenedApp: (app: any) => void
  chatbotContext: {
    selectedMessage: any
    setSelectedMessage: (msg: any) => void
    currentChat: { id: any; title: any }
    selectedAgent?: { name?: string } | null
  }
  abortSignal?: AbortSignal
}

export interface FrontendToolResult {
  success: boolean
  message: string
  data?: any
}

export interface FrontendToolHandler {
  definition: FrontendToolDefinition
  /** If provided, the tool is only included in the payload when this returns true */
  enabled?: () => boolean
  execute: (args: Record<string, any>, context: FrontendToolContext) => Promise<FrontendToolResult>
}

/** A ToolCall that has been parsed and is awaiting user confirmation */
export interface PendingFrontendToolCall {
  toolCall: ToolCall
  parsedArgs: Record<string, any>
  handler: FrontendToolHandler
  resolve: (result: FrontendToolResult) => void
}

// Utility functions

/** Create a success result */
export function createSuccessResult(message: string, data?: any): FrontendToolResult {
  return data ? { success: true, message, data } : { success: true, message }
}

/** Create an error result */
export function createErrorResult(message: string): FrontendToolResult {
  return { success: false, message }
}

/** Check if a handler requires user confirmation */
export function requiresConfirmation(handler: FrontendToolHandler): boolean {
  return handler.definition.requiresConfirmation === true
}

/** Validate that a result has required fields */
export function isValidResult(result: any): result is FrontendToolResult {
  return (
    result !== null &&
    typeof result === 'object' &&
    typeof result.success === 'boolean' &&
    typeof result.message === 'string'
  )
}
